import React, { useEffect, useRef, useState } from "react";

export default function TimelineScroll({ items }) {
    const [activeIndex, setActiveIndex] = useState(null);
    const timelineRef = useRef(null);
    const progressRef = useRef(null);
    const itemRefs = useRef([]);
    const [hasScrolled, setHasScrolled] = useState(false);


    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);
    // Observe which item is active
    useEffect(() => {
        const handleScroll = () => {
            let closestIndex = -1;
            let minDistance = Infinity;

            const viewportCenter = window.innerHeight / 2;

            itemRefs.current.forEach((item, index) => {
                if (!item) return;
                const rect = item.getBoundingClientRect();
                const itemCenter = rect.top + rect.height / 2;
                const distance = Math.abs(itemCenter - viewportCenter);

                if (distance < minDistance) {
                    minDistance = distance;
                    closestIndex = index;
                }
            });

            if (closestIndex !== -1) {
                setActiveIndex(closestIndex);
            }
        };

        const onScroll = () => {
            requestAnimationFrame(handleScroll);
        };

        window.addEventListener("scroll", onScroll);
        handleScroll(); // call initially

        return () => {
            window.removeEventListener("scroll", onScroll);
        };
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            const scrollY = window.scrollY || document.documentElement.scrollTop;
            if (scrollY > 50) {
                setHasScrolled(true);
            } else {
                setHasScrolled(false);
            }
        };

        // Listen to scroll only, don't trigger manually
        window.addEventListener("scroll", handleScroll);

        // Remove scroll listener on cleanup
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Smooth progress line update based on scroll position
    // useEffect(() => {
    //     let animationFrameId = null;

    //     const handleScroll = () => {
    //         if (animationFrameId) return; // prevent multiple calls

    //         animationFrameId = requestAnimationFrame(() => {
    //             if (!timelineRef.current || !progressRef.current) return;

    //             const timeline = timelineRef.current;
    //             const progress = progressRef.current;

    //             const scrollTop = window.scrollY || document.documentElement.scrollTop;
    //             const windowHeight = window.innerHeight;
    //             const offsetTop = timeline.offsetTop;
    //             const timelineHeight = timeline.offsetHeight;

    //             const scrollPos = scrollTop + windowHeight / 2;
    //             const percent = Math.min(
    //                 Math.max(((scrollPos - offsetTop) / timelineHeight) * 100, 0),
    //                 100
    //             );

    //             progress.style.height = `${percent}%`;
    //             animationFrameId = null;
    //         });
    //     };

    //     window.addEventListener("scroll", handleScroll);

    //     return () => {
    //         window.removeEventListener("scroll", handleScroll);
    //         if (animationFrameId) cancelAnimationFrame(animationFrameId);
    //     };
    // }, []);
    useEffect(() => {
        const progress = progressRef.current;
        const timeline = timelineRef.current;

        if (!progress || !timeline) return;

        const updateProgress = () => {
            const rect = timeline.getBoundingClientRect();
            const windowHeight = window.innerHeight;

            const totalScrollable = timeline.offsetHeight - windowHeight;
            const earlyOffset = window.innerHeight * 0.45;
            const scrolled = Math.min(Math.max(-rect.top + earlyOffset, 0), totalScrollable);


            const percent = (scrolled / totalScrollable) * 100;
            progress.style.height = `${percent}%`;

            requestAnimationFrame(updateProgress); // tight loop syncs with scroll bar
        };

        requestAnimationFrame(updateProgress);

        return () => cancelAnimationFrame(updateProgress);
    }, []);


    return (
        <div ref={timelineRef} className="relative w-full py-20 bg-white">
            <div className="max-w-7xl mx-auto px-4">
                <div className="relative">
                    {/* Unfilled full vertical line */}
                    <div className="absolute left-1/2 transform -translate-x-1/2 top-0 bottom-0 w-1 bg-gray-300 z-0" />

                    {/* Filled scroll progress line */}
                    <div
                        ref={progressRef}
                        className="absolute left-1/2 transform -translate-x-1/2 top-0 w-1 bg-gradient-to-b from-blue-400 via-blue-500 to-blue-600 transition-all duration-300 z-10"
                        style={{ height: "0%" }}
                    />

                    {/* Timeline Items */}
                    <div className="space-y-0 relative z-20">
                        {items.map((item, index) => {
                            const isVideo = !!item.video;
                            const isFirst = index === 0;
                            const isActive = activeIndex === index;
                            const isDisconnection = item.suffix === "years" && !item.text;
                            // const hasContent = item.suffix === "BCE" || item.text;
                            // Add fade/hide class to first item
                            const containerClasses = `relative ${isFirst && !hasScrolled ? "opacity-0 translate-y-10" : "opacity-100 translate-y-0"} transition-all duration-700 ease-out`;

                            return (
                                <div
                                    key={item.key}
                                    data-index={index}
                                    ref={(el) => (itemRefs.current[index] = el)}
                                    className={containerClasses}
                                >
                                    {isDisconnection && (
                                        <>
                                            {/* Line breaker to hide the vertical line behind */}
                                            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-[100px] bg-white z-20" />

                                            {/* Animated horizontal caps */}
                                            <div className="absolute left-1/2 transform -translate-x-1/2 z-40 flex flex-col items-center gap-24">
                                                <div className={`w-20 h-1 transition-colors duration-500 ${isActive ? "bg-blue-600" : "bg-gray-300"}`} />
                                                <div className={`w-20 h-1 transition-colors duration-500 ${isActive ? "bg-blue-600" : "bg-gray-300"}`} />
                                            </div>
                                        </>
                                    )}




                                    {/* VIDEO */}
                                    {isVideo ? (
                                        <div
                                            className={`transition-all duration-1000 py-20 ${isActive ? "opacity-100 scale-100" : "opacity-0 scale-95"
                                                }`}
                                        >
                                            <div className="max-w-3xl mx-auto">
                                                <div className="rounded-xl shadow-lg overflow-hidden border-4 border-blue-500">
                                                    <video
                                                        width="100%"
                                                        height="450"
                                                        controls
                                                        playsInline
                                                        preload="metadata"
                                                        className="w-full"
                                                        poster={item.video.poster || undefined} // optional thumbnail
                                                    >
                                                        <source src={item.video.id} type="video/mp4" />
                                                        Your browser does not support the video tag.
                                                    </video>
                                                </div>

                                                {item.caption && (
                                                    <p className="text-center text-gray-600 text-sm mt-4">
                                                        {item.caption}
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                    ) : isDisconnection ? (
                                        /* Only year — disconnected point */
                                        <div className="relative py-7">
                                            <div className="flex gap-6 md:gap-12 items-center justify-center">
                                                {/* LEFT: Year + suffix */}
                                                <div
                                                    className={`w-5/12 text-right transition-all duration-1000 ${isActive ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}
                                                >
                                                    <div className="text-3xl md:text-4xl font-bold text-blue-600">
                                                        {item.year}
                                                    </div>
                                                    {item.suffix && (
                                                        <div className="text-sm text-blue-400 mt-1">
                                                            {item.suffix}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* DOT */}
                                                {/* <div className="absolute left-1/2 transform -translate-x-1/2 z-40">
                                                    <div    
                                                        className={`w-5 h-5 rounded-full border-4 transition-all duration-500 ease-in-out ${isActive ? "bg-blue-600 border-blue-300 scale-125 shadow-lg" : "bg-white border-gray-300"}`}
                                                    />
                                                </div> */}

                                                {/* Empty right */}
                                                <div className="w-6/12" />
                                            </div>
                                        </div>
                                    ) : (
                                        /* Regular Content Item */
                                        <div className="relative py-16">
                                            <div className="flex gap-6 md:gap-24 items-stretch">
                                                {/* LEFT: Year */}
                                                <div
                                                    className={`w-1/2 text-right transition-all duration-1000 ${isActive ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}
                                                >
                                                    <div className="text-3xl md:text-4xl font-bold text-blue-600">
                                                        {item.year}
                                                    </div>
                                                    {item.suffix && (
                                                        <div className="text-sm text-blue-400 mt-1">
                                                            {item.suffix}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* DOT */}
                                                <div className="absolute left-1/2 transform -translate-x-1/2 z-40">
                                                    <div
                                                        className={`w-5 h-5 rounded-full border-4 transition-all duration-500 ease-in-out ${isActive ? "bg-blue-600 border-blue-300 scale-125 shadow-lg" : "bg-white border-gray-300"}`}
                                                    />
                                                </div>

                                                {/* RIGHT: Content */}
                                                <div
                                                    className={`w-6/12 transition-all duration-1000 ${isActive ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}
                                                >

                                                    <div
                                                        className="text-gray-600 text-sm leading-relaxed mb-4 [&_a]:text-blue-600 [&_a:hover]:underline"
                                                        dangerouslySetInnerHTML={{ __html: item.text }}
                                                    />


                                                    {item.image && (
                                                        <div className="overflow-hidden max-w-full mx-auto">
                                                            <img
                                                                src={item.image}
                                                                alt={item.title || "Timeline image"}
                                                                className=" max-h-[300px] w-auto object-contain"
                                                            />
                                                            {item.caption && (
                                                                <div className="py-3 text-xs text-gray-600">
                                                                    {item.caption}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}

                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}
